import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, query, where, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../Help/helper';
import BreadCrumb from '../BreadCrumb';
import moment from 'moment';
import logger from '../../utils/logger';
import { useAuth } from '../Auth/Auth';

const pages = [
  { name: 'Procurement', to: '/procurement', component: Link, current: false },
  { name: 'Approvals', to: '/procurement/approval', component: Link, current: true },
];

const ProcurementApproval = () => {
  const [procurements, setProcurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [selectedProcurement, setSelectedProcurement] = useState(null);
  const [approvalComment, setApprovalComment] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const { currentUser } = useAuth();

  // Stats
  const [stats, setStats] = useState({
    pending: 0,
    approvedToday: 0,
    rejectedToday: 0,
  });

  useEffect(() => {
    fetchPendingProcurements();
  }, []);

  const fetchPendingProcurements = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'procurements'),
        where('status', '==', 'Pending')
      );
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProcurements(items);

      // Calculate stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const approvedTodayQuery = query(
        collection(db, 'procurements'),
        where('status', '==', 'Approved'),
        where('statusUpdateDate', '>=', Timestamp.fromDate(today))
      );
      const approvedSnapshot = await getDocs(approvedTodayQuery);

      const rejectedTodayQuery = query(
        collection(db, 'procurements'),
        where('status', '==', 'Rejected'),
        where('statusUpdateDate', '>=', Timestamp.fromDate(today))
      );
      const rejectedSnapshot = await getDocs(rejectedTodayQuery);

      setStats({
        pending: items.length,
        approvedToday: approvedSnapshot.size,
        rejectedToday: rejectedSnapshot.size,
      });
    } catch (error) {
      logger.error('Error fetching procurements:', error);
      toast.error('Failed to load pending procurements');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (procurement, action) => {
    setSelectedProcurement(procurement);
    setActionType(action);
    setApprovalComment('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProcurement(null);
    setApprovalComment('');
    setActionType('');
  };

  const handleApprovalAction = async () => {
    if (!selectedProcurement) return;

    const newStatus = actionType === 'approve' ? 'Approved' : 'Rejected';

    try {
      setApproving(true);
      const procurementRef = doc(db, 'procurements', selectedProcurement.id);

      await updateDoc(procurementRef, {
        status: newStatus,
        approvalComment: approvalComment,
        statusUpdateDate: Timestamp.now(),
        approvedBy: currentUser?.displayName || currentUser?.email || 'System',
        approvedAt: Timestamp.now(),
      });

      // If approved, add to payables
      if (newStatus === 'Approved') {
        await addDoc(collection(db, 'payables'), {
          ...selectedProcurement,
          status: newStatus,
          approvalComment: approvalComment,
          statusUpdateDate: Timestamp.now(),
          approvedBy: currentUser?.displayName || currentUser?.email || 'System',
          approvedAt: Timestamp.now(),
        });
      }

      toast.success(`Procurement ${newStatus.toLowerCase()} successfully`);
      handleCloseModal();
      fetchPendingProcurements(); // Refresh list
    } catch (error) {
      logger.error('Error updating procurement:', error);
      toast.error('Failed to update procurement');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <>
        <BreadCrumb pages={pages} />
        <div className="min-h-screen bg-gradient-to-br from-amber-500 to-orange-600 py-4">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-white"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <BreadCrumb pages={pages} />
      <div className="min-h-screen bg-gradient-to-br from-amber-500 to-orange-600 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-black text-white">Procurement Approvals</h1>
                <p className="text-amber-50 text-sm mt-1">
                  Review and approve pending procurement requests
                </p>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white/95 backdrop-blur-sm overflow-hidden shadow-lg rounded-xl p-4">
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">Pending Approval</p>
                  <p className="text-2xl font-black text-amber-600">{stats.pending}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-sm overflow-hidden shadow-lg rounded-xl p-4">
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">Approved Today</p>
                  <p className="text-2xl font-black text-green-600">{stats.approvedToday}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-sm overflow-hidden shadow-lg rounded-xl p-4">
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl">
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">Rejected Today</p>
                  <p className="text-2xl font-black text-red-600">{stats.rejectedToday}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pending Approvals List */}
          {procurements.length === 0 ? (
            <div className="bg-white/95 backdrop-blur-sm shadow-lg rounded-xl p-12">
              <div className="text-center">
                <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pending Approvals</h3>
                <p className="text-sm text-gray-600">All procurement requests have been reviewed</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {procurements.map((procurement) => (
                <div key={procurement.id} className="bg-white/95 backdrop-blur-sm shadow-lg rounded-xl overflow-hidden">
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold text-white">
                          {procurement.procurementNumber}
                        </span>
                        <span className="text-sm font-semibold text-white">
                          {moment(procurement.dateRequested?.toDate?.() || procurement.dateRequested).format('MMM DD, YYYY')}
                        </span>
                      </div>
                      <span className="px-3 py-1 bg-white rounded-full text-xs font-bold text-amber-700">
                        Pending Review
                      </span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                      {/* Requester Info */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Requester</h4>
                        <p className="text-sm font-semibold text-gray-900">{procurement.requestedBy || 'N/A'}</p>
                        <p className="text-xs text-gray-600 mt-1">{procurement.department || ''}</p>
                      </div>

                      {/* Total Amount */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Total Amount</h4>
                        <p className="text-xl font-black text-amber-600">{formatCurrency(procurement.grandTotal || 0)}</p>
                      </div>

                      {/* Items Count */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Items</h4>
                        <p className="text-sm font-semibold text-gray-900">{procurement.cartItems?.length || 0} item(s)</p>
                      </div>
                    </div>

                    {/* Items Summary */}
                    <div className="mb-6">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Requested Items</h4>
                      <div className="bg-gray-50 rounded-xl p-4 max-h-48 overflow-y-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">#</th>
                              <th className="px-2 py-1.5 text-left text-xs font-bold text-gray-700 uppercase">Description</th>
                              <th className="px-2 py-1.5 text-center text-xs font-bold text-gray-700 uppercase">Qty</th>
                              <th className="px-2 py-1.5 text-right text-xs font-bold text-gray-700 uppercase">Price</th>
                              <th className="px-2 py-1.5 text-right text-xs font-bold text-gray-700 uppercase">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(procurement.cartItems || []).map((item, index) => (
                              <tr key={index} className="border-b border-gray-100">
                                <td className="px-2 py-1.5 text-xs text-gray-700">{index + 1}</td>
                                <td className="px-2 py-1.5 text-xs font-medium text-gray-900">{item.description}</td>
                                <td className="px-2 py-1.5 text-xs text-center text-gray-700">{item.quantity}</td>
                                <td className="px-2 py-1.5 text-xs text-right text-gray-700">{formatCurrency(item.price)}</td>
                                <td className="px-2 py-1.5 text-xs text-right font-semibold text-gray-900">
                                  {formatCurrency(item.price * item.quantity)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Justification/Notes */}
                    {procurement.justification && (
                      <div className="mb-6">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Justification</h4>
                        <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3">{procurement.justification}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleOpenModal(procurement, 'approve')}
                        className="flex-1 inline-flex justify-center items-center px-4 py-2.5 rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 transition-all transform hover:scale-105"
                      >
                        <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Approve
                      </button>
                      <button
                        onClick={() => handleOpenModal(procurement, 'reject')}
                        className="flex-1 inline-flex justify-center items-center px-4 py-2.5 rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 transition-all transform hover:scale-105"
                      >
                        <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Reject
                      </button>
                      <Link
                        to={`/procurement/requisitions/${procurement.id}`}
                        className="inline-flex items-center px-4 py-2.5 rounded-xl shadow-lg text-sm font-semibold text-amber-700 bg-white hover:bg-amber-50 transition-all"
                      >
                        <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Approval/Rejection Modal */}
      {showModal && selectedProcurement && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" onClick={handleCloseModal}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className={`px-6 py-4 ${
                actionType === 'approve'
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600'
                  : 'bg-gradient-to-r from-red-600 to-rose-600'
              }`}>
                <h3 className="text-xl font-bold text-white flex items-center">
                  <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {actionType === 'approve' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                  </svg>
                  {actionType === 'approve' ? 'Approve' : 'Reject'} Procurement
                </h3>
                <p className="text-sm text-white/80 mt-1">
                  {selectedProcurement.procurementNumber}
                </p>
              </div>
              <div className="bg-white px-6 py-6">
                <div className="mb-4">
                  <p className="text-sm text-gray-700 mb-2">
                    You are about to <span className="font-bold">{actionType}</span> this procurement request for{' '}
                    <span className="font-bold">{formatCurrency(selectedProcurement.grandTotal || 0)}</span>.
                  </p>
                </div>
                <div className="mb-6">
                  <label htmlFor="approvalComment" className="block text-sm font-semibold text-gray-700 mb-2">
                    Comment {actionType === 'reject' && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    id="approvalComment"
                    rows={4}
                    value={approvalComment}
                    onChange={(e) => setApprovalComment(e.target.value)}
                    placeholder={`Add a comment for ${actionType === 'approve' ? 'approval' : 'rejection'}...`}
                    className="block w-full rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2.5 rounded-xl shadow-sm text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApprovalAction}
                    disabled={approving || (actionType === 'reject' && !approvalComment.trim())}
                    className={`flex-1 px-4 py-2.5 rounded-xl shadow-lg text-sm font-semibold text-white transition-all ${
                      actionType === 'approve'
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                        : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {approving ? 'Processing...' : `Confirm ${actionType === 'approve' ? 'Approval' : 'Rejection'}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProcurementApproval;
